import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PageHeader, DashboardCard, StatusBadge, Button } from '@zafroi/ui-kit';
import { useProposal, useProposalTransition } from '@/hooks/useCommercial';
import { useCustomerNameMap } from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { proposalStatusVariants } from '@/config/commercial-status';
import { shortDate } from '@/lib/format';
import type { KoshProposalStatus } from '@/types/commercial-docs';

const ACTIONS: Partial<Record<KoshProposalStatus, { to: KoshProposalStatus; label: string }[]>> = {
  DRAFT: [{ to: 'SENT', label: 'Send' }, { to: 'REJECTED', label: 'Reject' }],
  SENT: [{ to: 'UNDER_REVIEW', label: 'Mark under review' }, { to: 'ACCEPTED', label: 'Accept' }, { to: 'REJECTED', label: 'Reject' }],
  UNDER_REVIEW: [{ to: 'ACCEPTED', label: 'Accept' }, { to: 'REJECTED', label: 'Reject' }],
  ACCEPTED: [{ to: 'CONVERTED', label: 'Convert' }],
};

export function ProposalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const q = useProposal(id);
  const nameMap = useCustomerNameMap();
  const transition = useProposalTransition();

  return (
    <div className="space-y-6">
      <QueryStateGuard data={q.data} isLoading={q.isLoading} isError={q.isError}>
        {(x) => {
          const actions = ACTIONS[x.status] ?? [];
          const sections = x.sections ?? [];
          return (
            <>
              <PageHeader
                eyebrow={<Link to="/proposals" className="inline-flex items-center gap-1 hover:text-foreground"><ArrowLeft className="h-3.5 w-3.5" /> Proposals</Link>}
                title={x.proposalNumber}
                meta={<StatusBadge status={x.status} variants={proposalStatusVariants} />}
                actions={
                  <div className="flex gap-2">
                    {actions.map((a, i) => (
                      <Button key={a.to} size="sm" variant={i === 0 ? 'default' : 'outline'}
                        disabled={transition.isPending} onClick={() => id && transition.mutate({ id, to: a.to })}>
                        {a.label}
                      </Button>
                    ))}
                  </div>
                }
              />
              <DashboardCard title="Overview">
                <div className="space-y-1.5 text-[13px]">
                  <Row k="Customer" v={nameMap.get(x.customerId) ?? x.customerId.slice(0, 8)} />
                  <Row k="Valid until" v={x.validUntil ? shortDate(x.validUntil) : '—'} />
                  {x.scopeSummary && <Row k="Scope" v={x.scopeSummary} />}
                  {x.notes && <Row k="Notes" v={x.notes} />}
                </div>
              </DashboardCard>
              {sections.length > 0 && (
                <DashboardCard title="Sections">
                  <div className="space-y-4">
                    {sections.map((s, i) => (
                      <div key={i}>
                        <h4 className="text-[13px] font-semibold">{s.title}</h4>
                        <p className="whitespace-pre-wrap text-[13px] text-muted-foreground">{s.body}</p>
                      </div>
                    ))}
                  </div>
                </DashboardCard>
              )}
            </>
          );
        }}
      </QueryStateGuard>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return <div className="flex justify-between gap-4"><span className="text-muted-foreground">{k}</span><span className="text-right">{v}</span></div>;
}
